// WHAT: Tab component for aggregated analytics with multiple sub-tabs
// WHY:  Provides comprehensive analytics interface with day tracking, insights, and breakdowns
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: Multiple chart, table, and tab components
// RISKS:
//   - NOTE: Tab structure is complex - may be confusing for users
//   - NOTE: Filter placement may not be intuitive
//   - NOTE: Some components may have performance issues with large datasets

import type { FC } from "react";

import { Tab, TabGroup, TabList, TabPanels, TabPanel } from "@headlessui/react";

import { Card } from "@/components/ui";
import { FilterSetting } from "@/domains/productivity/timetracq/components/molecules/FilterSetting";
import { TopCategoryCard } from "@/domains/productivity/timetracq/components/molecules/TopCategoryCard";
import { TotalTimeCard } from "@/domains/productivity/timetracq/components/molecules/TotalTimeCard";
import { AspectBarChart } from "@/domains/productivity/timetracq/components/organisms/AspectBarChart";
import { CategoryDonutChart } from "@/domains/productivity/timetracq/components/organisms/CategoryDonutChart";
import { TimeEntriesTable } from "@/domains/productivity/timetracq/components/organisms/TimeEntriesTable";
import { TrackedTodayTable } from "@/domains/productivity/timetracq/components/organisms/TrackedTodayTable";

import { AspectsTab } from "./AspectsTab";
import { DayTrack } from "./DayTrack";
import { InsightsTab } from "./InsightsTab";
import { WorkspacesTab } from "./WorkspacesTab";

/**
 * Tab names
 */
const tabs = ["Summary", "Day Track", "Time Entries", "Insights", "Workspaces", "Aspects"];

/**
 * WHAT:
 *   Tab component that provides comprehensive analytics interface with multiple
 *   sub-tabs for different analysis perspectives including day tracking, insights,
 *   and workspace/aspect breakdowns.
 *
 * WHY:
 *   Enables users to analyze their time tracking data from various angles with
 *   detailed visualizations and breakdowns.
 *
 * BEHAVIOR:
 *   - Displays tab navigation with filter controls
 *   - Shows summary with cards and charts
 *   - Provides day-by-day tracking view
 *   - Shows time entries table
 *   - Displays insights and breakdowns
 *   - Updates when filter changes
 *
 * RISKS:
 *   - NOTE: Tab structure is complex - may be confusing for users
 *   - NOTE: Filter placement may not be intuitive
 *   - NOTE: Some components may have performance issues with large datasets
 *
 * @returns {JSX.Element} Aggregated view tab component
 *
 */
export const AggregatedViewTab: FC = () => {
  return (
    <div className="h-full flex flex-col space-y-6">
      {/* Header */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
        padding="lg"
        variant="elevated"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-tracq-deep-orange/20 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-tracq-deep-orange"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">Analytics & Insights</h3>
              <p className="text-slate-400 text-sm">
                Detaillierte Analyse deiner Arbeitszeit und Produktivität
              </p>
            </div>
          </div>
        </div>
      </Card>

      <TabGroup>
        <TabList className="flex space-x-2 border-b border-slate-600/50 pb-2 mb-4">
          {tabs.map((tab) => (
            <Tab
              className={({ selected }) =>
                `px-3 py-1.5 rounded-xl text-sm font-medium focus:outline-none transition-all duration-200 ${
                  selected
                    ? "bg-tracq-bright-blue text-white"
                    : "text-slate-400 hover:text-slate-300 hover:bg-slate-600/50"
                }`
              }
              key={tab}
            >
              {tab}
            </Tab>
          ))}
          {/* Filter rechts neben "Aspects" */}
          <div className="ml-auto">
            <FilterSetting />
          </div>
        </TabList>

        <TabPanels className="flex-1 space-y-4">
          {/* Summary */}
          <TabPanel>
            <div className="grid gap-4 md:grid-cols-2">
              <TotalTimeCard />
              <TopCategoryCard />
              <AspectBarChart />
              <CategoryDonutChart />
              <div className="md:col-span-2">
                <TrackedTodayTable />
              </div>
            </div>
          </TabPanel>

          {/* Day Track */}
          <TabPanel>
            <DayTrack />
          </TabPanel>

          {/* Time Entries */}
          <TabPanel>
            <TimeEntriesTable />
          </TabPanel>

          {/* Insights */}
          <TabPanel>
            <InsightsTab />
          </TabPanel>

          {/* Workspaces */}
          <TabPanel>
            <WorkspacesTab />
          </TabPanel>

          {/* Aspects */}
          <TabPanel>
            <AspectsTab />
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </div>
  );
};

// Named export only - no default export
